/* REFACTORED: Cart cross-sell state engine v2. Client-side rendering via /products/{handle}.json.
   Hooks theme:cart::added, theme:cart::removed, and storage events. No Section Rendering API.
   Targets: Cart page/drawer cross-sell module. */
class CartCrossSell extends HTMLElement {
  constructor() {
    super();
    this.handles = {
      // 1. SINGLES (Update these to EXACT live handles, or the "Cream Only" state will render blank)
      diabSingle: 'diabetic-socks-solid-black-ftd710c91', 
      compSingle: 'solid-cotton-black-energy-socks',
      cream: 'dr-johnnys-foot-cream',
      
      // 2. DYNAMIC BUNDLES (Option B: Strict Variety)
      diabBundles: {
        default: '3-pack-diabetic-socks-stripe-pink-blue-purple', // Fallback (Stripes)
        alternate: '3-pack-diabetic-socks-polka-dot-pink-blue-purple' // <-- UPDATE to your real Polka Dot/Solid handle
      },
      compBundles: {
        default: '3-pack-compression-socks-stripe-sleeve', // Fallback (Stripes)
        alternate: '3-pack-compression-socks-solid-knee-high' // <-- UPDATE to your real Polka Dot/Solid handle
      }
    };
    this._productCache = {};
    this._rendering = false;

    document.addEventListener('theme:cart::added', () => this._debouncedInit());
    document.addEventListener('theme:cart::removed', () => this._debouncedInit());

    window.addEventListener('storage', (e) => {
      if (e.key === 'cartCurrentData') this._debouncedInit();
    });

    this.init();
  }

  _debouncedInit() {
    clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => this.init(), 300);
  }

  async init() {
    if (this._rendering) return;
    this._rendering = true;

    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      const state = this.determineState(cart);

      if (!state) {
        this.innerHTML = '';
        return;
      }

      const [bundleProduct, secondaryProduct] = await Promise.all([
        this._fetchProduct(state.bundle),
        this._fetchProduct(state.secondary)
      ]);

      this.innerHTML = this._renderModule(
        [bundleProduct, secondaryProduct].filter(Boolean)
      );
    } catch (err) {
      console.error('[CartCrossSell] init error:', err);
    } finally {
      this._rendering = false;
    }
  }

  async _fetchProduct(handle) {
    if (!handle) return null;
    if (this._productCache[handle]) return this._productCache[handle];

    try {
      const res = await fetch(`/products/${handle}?section_id=product-json-data`);
      if (!res.ok) return null;
      const htmlText = await res.text();
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlText;
      const rawJson = tempDiv.textContent.trim();
      const data = JSON.parse(rawJson);
      this._productCache[handle] = data;
      return data;
    } catch (err) {
      console.error(`[CartCrossSell] Failed to fetch ${handle}:`, err);
      return null;
    }
  }

  determineState(cart) {
    const items = cart.items;
    const socks = items.filter(i => i.handle !== this.handles.cream);

    /* Edge Case: No socks in cart (Only foot cream) */
    if (socks.length === 0) {
      return { bundle: this.handles.diabSingle, secondary: this.handles.compSingle };
    }

    const hasDiab3 = items.some(i => i.handle.includes('3-pack-diabetic-socks'));
    const hasComp3 = items.some(i => i.handle.includes('3-pack-compression-socks'));

    /* 1: Global Exclusion — both 3-packs in cart */
    if (hasDiab3 && hasComp3) return null;

    /* 2: Category Winner & Pattern Detection */
    let diabQty = 0, compQty = 0;
    let diabSub = 0, compSub = 0;
    let cartHasStripes = false;

    socks.forEach(item => {
      // Tally Category
      if (item.handle.includes('diabetic')) {
        diabQty += item.quantity;
        diabSub += item.line_price;
      } else if (item.handle.includes('compression')) {
        compQty += item.quantity;
        compSub += item.line_price;
      }

      // Detect Patterns for Strict Variety
      if (item.handle.includes('stripe')) {
        cartHasStripes = true;
      }
    });

    let winner = 'compression';
    if (diabQty > compQty) winner = 'diabetic';
    else if (diabQty === compQty && diabSub > compSub) winner = 'diabetic';

    /* 3: Resolve Dynamic Bundle */
    let bundle = '';
    let secondary = this.handles.cream;

    if (winner === 'compression') {
      if (hasComp3) {
        // They have a compression 3-pack -> Recommend Diabetic
        bundle = cartHasStripes ? this.handles.diabBundles.alternate : this.handles.diabBundles.default;
      } else {
        bundle = cartHasStripes ? this.handles.compBundles.alternate : this.handles.compBundles.default;
      }
    } else {
      if (hasDiab3) {
        // They have a diabetic 3-pack -> Recommend Compression
        bundle = cartHasStripes ? this.handles.compBundles.alternate : this.handles.compBundles.default;
      } else {
        bundle = cartHasStripes ? this.handles.diabBundles.alternate : this.handles.diabBundles.default;
      }
    }

    return { bundle, secondary };
  }

  _renderModule(products) {
    if (!products.length) return '';

    const cardsHtml = products.map(p => this._renderCard(p)).join('');

    return `
      <div class="cross-sell-routine">
        <div class="cross-sell-routine__header">
          <h2 class="h3">The complete routine</h2>
          <p>Apply the cream at night. Wear the socks all day. Together, they go further.</p>
        </div>
        <div class="cross-sell-routine__grid">
          ${cardsHtml}
        </div>
      </div>
    `;
  }

  _renderCard(product) {
    /* .js endpoint: featured_image is a URL string, not { src: ... } */
    const rawImg = product.featured_image || (product.images && product.images[0]) || '';
    const imgSrc = rawImg
      ? rawImg.replace(/\.(png|jpg|jpeg|webp|gif)/i, '_600x.$1')
      : '';
    const url = `/products/${product.handle}`;
    const price = this._formatMoney(product.variants?.[0]?.price);
    const availableVariants = (product.variants || []).filter(v => v.available);

    const optionsHtml = availableVariants.map(v =>
      `<option value="${v.id}" data-value="${v.id}">${this._escapeHtml(v.title)}</option>`
    ).join('');

    const hasMultipleVariants = availableVariants.length > 1;
    
    // Using a native <select> bypasses theme JS conflicts and guarantees desktop/mobile clicks work
    const selectBlock = hasMultipleVariants ? `
      <div class="cross-sell-select-wrapper" style="margin-bottom: 15px;">
        <select name="id" class="product-options__native" style="width: 100%; padding: 12px; border: 1px solid #e2e2e2; border-radius: 4px; background: #fff; cursor: pointer; font-size: 14px;">
          ${optionsHtml}
        </select>
      </div>
    ` : `<input type="hidden" name="id" value="${availableVariants[0]?.id || ''}">`;

    return `
      <div class="cross-sell-card" data-js-product>
        <div class="cross-sell-card__image">
          <a href="${url}">
            <img src="${imgSrc}" alt="${this._escapeHtml(product.title)}" loading="lazy">
          </a>
        </div>
        <div class="cross-sell-card__info">
          <h3 class="h5"><a href="${url}">${this._escapeHtml(product.title)}</a></h3>
          <div class="price">${price}</div>
        </div>
        <form method="post" action="/cart/add" class="cross-sell-card__form js-product-form"
              accept-charset="UTF-8" enctype="multipart/form-data" novalidate="novalidate"
              data-type="add-to-cart-form" data-js-product-form>
          <input type="hidden" name="form_type" value="product">
          <input type="hidden" name="utf8" value="✓">
          ${selectBlock}
          <button type="submit"
                  class="btn btn--full btn--status js-add-to-cart js-product-button-add-to-cart"
                  name="add" data-js-product-button-add-to-cart>
            <span class="d-flex flex-center">
              <span class="btn__text">Add to Cart</span>
            </span>
          </button>
        </form>
      </div>
    `;
  }

  _formatMoney(cents) {
    if (!cents && cents !== 0) return '';
    if (window.Shopify?.formatMoney) {
      return Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
    }
    return '$' + (cents / 100).toFixed(2);
  }

  _escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }
}
customElements.define('cart-cross-sell', CartCrossSell);
